import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as svc from "../../api/userService";
import { getApiErrorMessage } from "../../utils/apiError";

const rid = (r) => r.roleID ?? r.id;

export const fetchUsers = createAsyncThunk("users/fetchAll", async () => {
  const res = await svc.getUsers();
  return res.data;
});

export const createUserThunk = createAsyncThunk("users/create", async (data, { rejectWithValue }) => {
  try { await svc.createUser(data); } catch (_) { /* backend saves but returns non-2xx */ }
  try {
    const res = await svc.getUsers();
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const updateUserThunk = createAsyncThunk("users/update", async ({ id, data }, { rejectWithValue }) => {
  try { await svc.updateUser(id, data); } catch (_) { /* backend saves but returns non-2xx */ }
  try {
    const res = await svc.getUsers();
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const deleteUserThunk = createAsyncThunk("users/delete", async (id, { rejectWithValue }) => {
  try { await svc.deleteUser(id); } catch (_) { /* backend deletes but may return non-2xx */ }
  try {
    const res = await svc.getUsers();
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const fetchRoles = createAsyncThunk("users/fetchRoles", async () => {
  const res = await svc.getRoles();
  return res.data;
});

export const createRoleThunk = createAsyncThunk("users/createRole", async (data, { rejectWithValue }) => {
  try {
    await svc.createRole(data);
    const res = await svc.getRoles();
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to create role."));
  }
});

export const updateRoleThunk = createAsyncThunk("users/updateRole", async ({ id, data }, { rejectWithValue }) => {
  try {
    await svc.updateRole(id, data);
    const res = await svc.getRoles();
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to update role."));
  }
});

export const deleteRoleThunk = createAsyncThunk("users/deleteRole", async (id, { rejectWithValue }) => {
  try {
    await svc.deleteRole(id);
    return id;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to delete role."));
  }
});

const userSlice = createSlice({
  name: "users",
  initialState: {
    list: [],
    roles: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUsers.fulfilled, (state, action) => { state.loading = false; state.list = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchUsers.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
      .addCase(createUserThunk.fulfilled, (state, action) => { state.list = Array.isArray(action.payload) ? action.payload : state.list; })
      .addCase(updateUserThunk.fulfilled, (state, action) => { state.list = Array.isArray(action.payload) ? action.payload : state.list; })
      .addCase(deleteUserThunk.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload) ? action.payload : state.list;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => { state.roles = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(createRoleThunk.fulfilled, (state, action) => { state.roles = Array.isArray(action.payload) ? action.payload : state.roles; })
      .addCase(updateRoleThunk.fulfilled, (state, action) => { state.roles = Array.isArray(action.payload) ? action.payload : state.roles; })
      .addCase(deleteRoleThunk.fulfilled, (state, action) => {
        state.roles = state.roles.filter((r) => rid(r) !== action.payload);
      });
  },
});

export default userSlice.reducer;
